import * as Yup from 'yup';
import { ValidationError } from 'yup';
import { MetaplanItemResponseDto } from '@tiendanube/common';

export const validationSchema = (
  existingMetaplans: MetaplanItemResponseDto[] = [],
) =>
  Yup.object().shape({
    name: Yup.string()
      .required('subscriptions.form.errors.nameRequired')
      .max(255, 'subscriptions.form.errors.nameMaxLength')
      .test(
        'unique',
        'subscriptions.form.errors.nameDuplicate',
        function (value) {
          if (!value) return true;
          return !existingMetaplans.some(
            (metaplan) =>
              metaplan.name.toLowerCase().trim() === value.toLowerCase().trim(),
          );
        },
      ),
    productIds: Yup.array()
      .of(Yup.number())
      .min(1, 'subscriptions.form.errors.productsRequired')
      .required('subscriptions.form.errors.productsRequired'),
    promotionCombinations: Yup.object()
      .shape({
        combinesWithPriceDiscounts: Yup.boolean().required(),
        combinesWithQuantityDiscounts: Yup.boolean().required(),
        combinesWithFreeShipping: Yup.boolean().required(),
        combinesWithCartAmountDiscounts: Yup.boolean().required(),
        combinesWithAppDiscounts: Yup.boolean().required(),
      })
      .required('subscriptions.form.errors.promotionCombinationsRequired'),
    isSubscriptionOnly: Yup.boolean().required(
      'subscriptions.form.errors.isSubscriptionOnlyRequired',
    ),
    subscriptionOptions: Yup.array()
      .of(
        Yup.object().shape({
          frequencyType: Yup.string().required(
            'subscriptions.form.errors.frequencyTypeRequired',
          ),
          frequencyParam: Yup.string()
            .required('subscriptions.form.errors.daysRequired')
            .test('min', 'subscriptions.form.errors.daysMin', function (value) {
              const numValue = parseInt(value || '0', 10);
              return numValue >= 5;
            }),
          discountPercentage: Yup.number()
            .required('subscriptions.form.errors.discountRequired')
            .test(
              'integer',
              'subscriptions.form.errors.discountMin',
              function (value) {
                if (value === undefined || value === null) return true;
                return Number.isInteger(value);
              },
            )
            .min(0, 'subscriptions.form.errors.discountMin')
            .max(100, 'subscriptions.form.errors.discountMax'),
        }),
      )
      .min(1, 'subscriptions.form.errors.frequenciesMin')
      .required('subscriptions.form.errors.frequenciesMin')
      .test(
        'unique-frequencies',
        'subscriptions.form.errors.daysDuplicate',
        function (subscriptionOptionsArray) {
          if (
            !subscriptionOptionsArray ||
            !Array.isArray(subscriptionOptionsArray)
          ) {
            return true;
          }

          const frequencies = subscriptionOptionsArray.map(
            (item) => item?.frequencyParam,
          );
          const seen = new Set<string>();
          const duplicateIndices = new Set<number>();

          frequencies.forEach((frequency, index) => {
            if (!frequency || frequency === '0') return;
            if (seen.has(frequency)) {
              duplicateIndices.add(index);
              const firstIndex = frequencies.findIndex((f) => f === frequency);
              if (firstIndex !== index) {
                duplicateIndices.add(firstIndex);
              }
            } else {
              seen.add(frequency);
            }
          });

          if (duplicateIndices.size > 0) {
            const errors: ValidationError[] = [];

            duplicateIndices.forEach((index) => {
              errors.push(
                this.createError({
                  path: `${this.path}[${index}].frequencyParam`,
                  message: 'subscriptions.form.errors.daysDuplicate',
                }),
              );
            });

            if (errors.length > 0) {
              return new ValidationError(errors);
            }
          }

          return true;
        },
      ),
  });
